import { AlertTriangle, CalendarDays, CheckCircle2, ClipboardList, Database, Landmark, Shield, Target } from 'lucide-react';

type ChecklistPhase = {
  phase: string;
  items: string[];
};

const integrationBlocks = [
  {
    title: 'PrismeProjet / Railway',
    points: ['Structure des tables', 'Ajustements pour scaler', 'Source of truth unique'],
  },
  {
    title: 'Microsoft 365',
    points: ['Courriel', 'Calendrier', 'Contacts', 'Environnement vanille dissocié de BZ'],
  },
  {
    title: 'OpenRouter / stratégie LLM',
    points: ['Backlog des tâches IA', 'Routing selon criticité / coût', 'Respect du budget cible 500 $/mois'],
  },
  {
    title: 'Cron / orchestration',
    points: ['Relances', 'Rappels', 'Escalades', 'Routines de suivi'],
  },
  {
    title: 'ERP Tickets',
    points: ['Pont d’alimentation projet', 'Synchronisation exécution terrain'],
  },
  {
    title: 'Logs / Audit IA',
    points: ['Journalisation des prompts', 'Modèles utilisés', 'Actions prises', 'Traçabilité complète'],
  },
];

const checklistPhases: ChecklistPhase[] = [
  {
    phase: 'Phase 1 — Cadre & gouvernance',
    items: [
      'Définir les règles de suivi',
      'Définir les types de projets',
      'Confirmer rôles et responsabilités',
      'Cadrer l’autonomie du bot',
    ],
  },
  {
    phase: 'Phase 2 — Fondation data',
    items: [
      'Revoir le schéma DB',
      'Confirmer tables projets / tâches / risques / décisions / logs',
      'Préparer la structure scalable',
    ],
  },
  {
    phase: 'Phase 3 — Core bot',
    items: [
      'Lire / écrire dans PrismeProjet',
      'Gérer tâches et statuts',
      'Créer les routines de suivi',
      'Préparer les crons',
    ],
  },
  {
    phase: 'Phase 4 — Intégrations M365',
    items: ['Connecter mail', 'Connecter calendrier', 'Connecter contacts', 'Enregistrer l’historique'],
  },
  {
    phase: 'Phase 5 — Stratégie LLM / budget',
    items: ['Backlog IA par type de tâche', 'Routing modèles', 'Contrôle coûts', 'Suivi consommation'],
  },
  {
    phase: 'Phase 6 — Pilote terrain',
    items: [
      'Charger 10 projets échantillons',
      'Tester la qualité des suivis',
      'Ajuster les règles',
      'Valider l’interface PRIsME',
    ],
  },
  {
    phase: 'Phase 7 — Industrialisation',
    items: ['Dashboard traçabilité', 'Logs IA', 'Pont ERP Tickets', 'Migration Asana', 'Go live TI / Cyber'],
  },
];

const majorRisks = [
  'DB mal structurée',
  'Bot trop complexe trop tôt',
  'Coûts IA non contrôlés',
  'Manque de traçabilité / auditabilité',
];

const principles = [
  'Source of truth unique = PrismeProjet',
  'Environnement Microsoft 365 isolé',
  'Deny by default dans la logique d’accès',
  'Journalisation complète des actions IA',
  'Simplicité avant sophistication',
  'PMBOK comme cadre de qualité de suivi',
];

export default function BotProjetIntegrationPlanTab() {
  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-indigo-400/20 bg-indigo-500/10 p-5">
        <div className="flex items-center gap-3 mb-2">
          <Target className="w-6 h-6 text-indigo-200" />
          <h3 className="text-2xl font-black text-white">Plan d’intégrations BotProjet</h3>
          <span className="inline-flex items-center rounded-full bg-emerald-500/20 border border-emerald-400/30 px-2.5 py-1 text-xs font-semibold text-emerald-200">
            60 jours
          </span>
        </div>
        <p className="text-sm text-slate-200 leading-7">
          Implantation du bot chargé de projet BZ selon une approche gouvernée, traçable, scalable et alignée PMBOK.
        </p>
      </div>

      <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <h4 className="text-lg font-bold text-white mb-2">Mandat</h4>
        <ul className="space-y-2 text-sm text-slate-200">
          <li>• Le bot agit comme chargé de projet autonome assisté.</li>
          <li>• Il orchestre le suivi des projets stratégiques, tactiques et opérationnels.</li>
          <li>• Il s’appuie sur PrismeProjet, Microsoft 365, OpenRouter et les intégrations ERP/Tickets.</li>
          <li>• Toute action IA doit être traçable, journalisée et gouvernée.</li>
        </ul>
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4">
          <div className="flex items-center gap-3 mb-2">
            <img
              src="/DB.png"
              alt="Dany Bourassa"
              className="w-10 h-10 rounded-full object-cover border border-cyan-300/40"
            />
            <h5 className="text-sm font-bold text-cyan-200">Dany — Owner</h5>
          </div>
          <ul className="space-y-2 text-sm text-slate-200">
            <li>• Approuve la stratégie de suivi.</li>
            <li>• Fixe les exigences.</li>
            <li>• Valide les arbitrages majeurs.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-4">
          <div className="flex items-center gap-3 mb-2">
            <img
              src="/PB.png"
              alt="Philippe Bolduc"
              className="w-10 h-10 rounded-full object-cover border border-indigo-300/40"
            />
            <h5 className="text-sm font-bold text-indigo-200">Philippe — Pilote / Qualité</h5>
          </div>
          <ul className="space-y-2 text-sm text-slate-200">
            <li>• Configure, teste, tune et valide la qualité du bot.</li>
            <li>• Ajuste les règles de suivi.</li>
            <li>• Contrôle la cohérence opérationnelle.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4">
          <h5 className="text-sm font-bold text-emerald-200 mb-2">BotProjet — Exécution</h5>
          <ul className="space-y-2 text-sm text-slate-200">
            <li>• Suit les projets.</li>
            <li>• Relance, documente, journalise.</li>
            <li>• Alimente les tableaux de bord.</li>
          </ul>
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-5">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-5 h-5 text-amber-200" />
            <h4 className="text-lg font-bold text-amber-200">Cible Jour 30</h4>
          </div>
          <ul className="space-y-2 text-sm text-slate-200">
            <li>• DB contrôlée par le bot.</li>
            <li>• Analyse de 10 projets échantillons.</li>
            <li>• Premiers crons fonctionnels.</li>
            <li>• Interface projet PRIsME ajustée.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-5">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-5 h-5 text-emerald-200" />
            <h4 className="text-lg font-bold text-emerald-200">Cible Jour 60</h4>
          </div>
          <ul className="space-y-2 text-sm text-slate-200">
            <li>• Migration des projets Asana.</li>
            <li>• Gestion 100% fonctionnelle TI / Cyber.</li>
            <li>• Pont ERP Tickets opérationnel.</li>
            <li>• Traçabilité IA complète.</li>
          </ul>
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-lg font-bold text-white">Blocs d’intégration</h4>
        <div className="grid lg:grid-cols-2 gap-4">
          {integrationBlocks.map((block) => (
            <div key={block.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm font-bold text-indigo-200 mb-2">{block.title}</div>
              <ul className="space-y-2 text-sm text-slate-200">
                {block.points.map((point) => (
                  <li key={point}>• {point}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-cyan-200" />
          <h4 className="text-lg font-bold text-white">Checklist d’exécution — 60 jours</h4>
        </div>
        <div className="space-y-3">
          {checklistPhases.map((phase) => (
            <div key={phase.phase} className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center rounded-full bg-indigo-500/20 border border-indigo-400/30 px-2 py-0.5 text-xs font-semibold text-indigo-200">
                  En cours
                </span>
                <h5 className="text-sm font-bold text-white">{phase.phase}</h5>
              </div>
              <ul className="space-y-2 text-sm text-slate-200">
                {phase.items.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-200" />
          <h4 className="text-lg font-bold text-white">Risques majeurs</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {majorRisks.map((risk) => (
            <div key={risk} className="rounded-2xl border border-rose-400/20 bg-rose-500/10 p-4 text-sm text-slate-200">
              • {risk}
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-indigo-400/20 bg-white/5 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Landmark className="w-5 h-5 text-indigo-200" />
          <h4 className="text-lg font-bold text-white">Principes directeurs</h4>
        </div>
        <div className="grid lg:grid-cols-2 gap-2">
          {principles.map((principle) => (
            <div key={principle} className="text-sm text-slate-200 flex items-start gap-2">
              <Shield className="w-4 h-4 text-indigo-300 mt-0.5 shrink-0" />
              <span>{principle}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-amber-200" />
          <p className="text-sm text-slate-200">
            Budget cible gouverné: <span className="font-semibold text-white">500 $ / mois</span>, contrôlé via backlog IA
            et routing OpenRouter par criticité.
          </p>
        </div>
      </section>
    </div>
  );
}

